import { useCallback, useEffect, useMemo, useState } from "react";
import type { AnyVariables } from "urql";
import { useQuery } from "urql";

export const PAGE_SIZE = 50;

export interface PageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface Connection<T> {
  edges: { node: T }[];
  pageInfo: PageInfo;
}

interface Options<TData, TNode> {
  query: Parameters<typeof useQuery>[0]["query"];
  /** Filter variables only — `first`/`after` are managed by the hook. */
  variables?: AnyVariables;
  /** Picks the connection out of the query result. */
  getConnection: (data: TData) => Connection<TNode> | null | undefined;
  pause?: boolean;
}

/**
 * Cursor pagination over a relay connection field with manual page
 * accumulation ("Load more"), for use with urql's document cache — which
 * does not merge relay pages itself. Changing the filter variables resets
 * the accumulated pages; `refresh` restarts from page one over the network.
 */
export function usePaginatedList<TData, TNode>({
  query,
  variables,
  getConnection,
  pause,
}: Options<TData, TNode>) {
  const [cursor, setCursor] = useState<string | null>(null);
  const [priorNodes, setPriorNodes] = useState<TNode[]>([]);

  // Reset accumulation whenever the filter variables change (e.g. a new
  // search term) so pages from different filters never interleave.
  const variablesKey = JSON.stringify(variables ?? {});
  // biome-ignore lint/correctness/useExhaustiveDependencies: variablesKey is the serialized form of variables
  useEffect(() => {
    setCursor(null);
    setPriorNodes([]);
  }, [variablesKey]);

  const [{ data, fetching, error }, reexecute] = useQuery({
    query,
    variables: { ...(variables ?? {}), first: PAGE_SIZE, after: cursor ?? undefined },
    // Revalidate over the network on every variable change (page loads,
    // filter changes) so pagination never serves a stale cached page.
    requestPolicy: "cache-and-network",
    pause,
  });

  const connection = data ? getConnection(data as TData) : undefined;
  const pageNodes = useMemo(() => connection?.edges.map((edge) => edge.node) ?? [], [connection]);
  const nodes = useMemo(() => [...priorNodes, ...pageNodes], [priorNodes, pageNodes]);
  const hasNextPage = connection?.pageInfo.hasNextPage ?? false;

  const loadMore = useCallback(() => {
    if (!connection?.pageInfo.hasNextPage || !connection.pageInfo.endCursor) return;
    setPriorNodes((prior) => [...prior, ...pageNodes]);
    setCursor(connection.pageInfo.endCursor);
  }, [connection, pageNodes]);

  const refresh = useCallback(() => {
    setPriorNodes([]);
    setCursor(null);
    reexecute({ requestPolicy: "network-only" });
  }, [reexecute]);

  return {
    nodes,
    data: data as TData | undefined,
    fetching,
    error,
    hasNextPage,
    loadMore,
    refresh,
  };
}
